import { Box, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { RecommendationPageContext } from '@kheops/graphql';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mapProductFragmentToProductCardData } from '../common/product-card.mappers';
import ProductHitCard from '../common/ProductHitCard';
import { productRecommendationsImageSizeByBreakpoint } from '../../discovery/state';
import { useProductsByIdsQuery } from '../../queries/__generated__/productsByIds.generated';
import { useUserProductRecommendationsQuery } from '../../queries/__generated__/userProductRecommendations.generated';
import { packagingSheetSelectedProductAtom, userAtom, currentContextAtom } from '../../state';
import DiscoveryRedirectCard from './DiscoveryRedirectCard';

export interface ProductRecommendationsProps {
  supplierId?: string;
  limit?: number;
  pageContext?: RecommendationPageContext;
  subFamily?: string;
}

export default function ProductRecommendations({
  supplierId,
  limit = 10,
  pageContext,
  subFamily,
}: ProductRecommendationsProps): React.JSX.Element | null {
  const { id: currentUserId } = useAtomValue(userAtom);
  const setSelectedProduct = useSetAtom(packagingSheetSelectedProductAtom);
  const { roleAssignmentId } = useAtomValue(currentContextAtom);
  const { t } = useTranslation(['products']);
  const theme = useTheme();
  const isUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const skipRecommendations = !currentUserId || !roleAssignmentId;

  const { data: recommendationsData, loading: recommendationsLoading } = useUserProductRecommendationsQuery({
    skip: skipRecommendations,
    variables: {
      userId: currentUserId!,
      roleAssignmentId: roleAssignmentId!,
      supplierId,
      limit,
      pageContext,
      subFamily,
    },
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  const recommendations = recommendationsData?.getUserProductRecommendations?.recommendations ?? [];

  const { data: productsData, loading: productsLoading } = useProductsByIdsQuery({
    skip: recommendationsLoading || !recommendations.length,
    variables: {
      where: {
        _or: recommendations.map(({ company_id, sku }) => ({
          company_id: { _eq: company_id },
          sku: { _eq: sku },
        })),
      },
      baseUnitsWhere: {
        _or: recommendations.map(({ company_id, base_unit_sku }) => ({
          company_id: { _eq: company_id },
          sku: { _eq: base_unit_sku },
        })),
      },
    },
  });

  const products = useMemo(() => {
    const productsByKey = new Map(
      (productsData?.product || []).map((product) => [
        `${product.company_id}:${product.sku}`,
        mapProductFragmentToProductCardData(product),
      ]),
    );

    return recommendations
      .map(({ company_id, sku }) => productsByKey.get(`${company_id}:${sku}`)!)
  }, [productsData, recommendations]);

  const loading = recommendationsLoading || productsLoading;

  if (skipRecommendations || loading || !products.length) {
    return null;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="headlineMedium" sx={{ mb: 3 }}>
        {t('products:our_best_sellers_for_you')}
      </Typography>

      <Tabs
        variant="scrollable"
        scrollButtons={isUpMd}
        sx={{
          '& .MuiTabs-list': {
            gap: 2,
          },
        }}
      >
        {products.map((product) => (
          <Tab
            key={product!.id}
            component={ProductHitCard}
            layout="grid"
            product={product}
            productImageSizeByBreakPoint={productRecommendationsImageSizeByBreakpoint}
            displayCompany
            displayPackaging
            displaySuggestedRetailPrice
            displayWishlistButton
            onProductClick={() => setSelectedProduct(product)}
            parentBoxSx={{
              width: { xs: 256, lg: 288, xl: 275 },
              flexShrink: 0,
            }}
          />
        ))}
        <Tab
          component={DiscoveryRedirectCard}
          parentBoxSx={{
            width: { xs: 256, lg: 288, xl: 275 },
            flexShrink: 0,
            whiteSpace: 'initial'
          }}
        />
      </Tabs>
    </Box>
  );
}
